import { useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import SearchSelect, { type SearchSelectOption } from '@/components/Shared/SearchSelect';
import {
  useNetzplanImportStore,
  type CustomerDecision,
  type CustomerSource,
} from '@/store/useNetzplanImportStore';
import type { DecisionAction } from '@/pages/Import/logic/types';
import { CategoryBadge } from './CategoryBadge';
import { StepFooter } from './StepFooter';

const INPUT =
  'w-full px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50';
const ROW = 'grid grid-cols-[minmax(0,1.4fr)_88px_96px_minmax(0,1.8fr)] gap-3 px-3 py-2 items-start';

export function CustomerStep() {
  const { t } = useTranslation('import');
  const { customerSources, customerDecisions, setCustomerDecision, live } = useNetzplanImportStore();
  const [onlyOpen, setOnlyOpen] = useState(false);

  const options: SearchSelectOption[] = useMemo(
    () =>
      live.customers.map((c) => ({
        id: c.id,
        label: c.name,
        description: c.customerNumber,
      })),
    [live.customers],
  );

  const rows = useMemo(
    () => (onlyOpen ? customerSources.filter((s) => s.category !== 'MATCH') : customerSources),
    [customerSources, onlyOpen],
  );

  const counts = useMemo(() => {
    let create = 0;
    let link = 0;
    let skip = 0;
    for (const s of customerSources) {
      const a = customerDecisions[s.id]?.action;
      if (a === 'create') create += 1;
      else if (a === 'link') link += 1;
      else if (a === 'skip') skip += 1;
    }
    return { create, link, skip };
  }, [customerSources, customerDecisions]);

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <p className="text-[13px] text-slate-600">{t('customers.hint')}</p>
          <label className="flex items-center gap-2 text-[12px] text-slate-600 whitespace-nowrap">
            <input
              type="checkbox"
              checked={onlyOpen}
              onChange={(e) => setOnlyOpen(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
            />
            {t('customers.onlyOpen')}
          </label>
        </div>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className={`${ROW} bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400`}>
            <span>{t('customers.colSource')}</span>
            <span>{t('customers.colCategory')}</span>
            <span>{t('customers.colAction')}</span>
            <span>{t('customers.colTarget')}</span>
          </div>

          <div className="max-h-[52vh] overflow-y-auto divide-y divide-slate-100">
            {rows.map((src) => (
              <CustomerRow
                key={src.id}
                src={src}
                decision={customerDecisions[src.id]}
                options={options}
                onChange={(patch) => setCustomerDecision(src.id, patch)}
              />
            ))}
            {rows.length === 0 && (
              <p className="px-3 py-6 text-center text-[12px] text-slate-500">{t('customers.empty')}</p>
            )}
          </div>
        </div>

        <StepFooter
          next="objects"
          summary={t('customers.summary', { create: counts.create, link: counts.link, skip: counts.skip })}
        />
      </div>
    </Card>
  );
}

function CustomerRow({
  src,
  decision,
  options,
  onChange,
}: {
  src: CustomerSource;
  decision: CustomerDecision | undefined;
  options: SearchSelectOption[];
  onChange: (patch: Partial<CustomerDecision>) => void;
}) {
  const { t } = useTranslation('import');
  if (!decision) return null;

  const suggested: SearchSelectOption[] = src.suggestion
    ? [{ id: src.suggestion.id, label: src.suggestion.name, description: src.suggestion.customerNumber }]
    : [];

  return (
    <div className={ROW}>
      <div className="min-w-0">
        <div className="text-[13px] text-slate-900 truncate">{src.rawName}</div>
        {src.isBucketChild && (
          <div className="text-[11px] text-amber-600">{t('customers.bucketChild')}</div>
        )}
      </div>

      <div className="pt-0.5">
        <CategoryBadge category={src.category} score={src.score} />
      </div>

      <select
        value={decision.action}
        onChange={(e) => onChange({ action: e.target.value as DecisionAction })}
        className={INPUT}
      >
        <option value="link">{t('actions.link')}</option>
        <option value="create">{t('actions.create')}</option>
        <option value="skip">{t('actions.skip')}</option>
      </select>

      <div className="min-w-0">
        {decision.action === 'link' && (
          <SearchSelect
            options={options}
            suggestedOptions={suggested}
            value={decision.linkedCustomerId}
            onChange={(id) => onChange({ linkedCustomerId: id })}
            placeholder={t('customers.pickCustomer')}
          />
        )}
        {decision.action === 'create' && (
          <div className="space-y-1.5">
            <input
              className={INPUT}
              value={decision.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder={t('customers.name')}
            />
            <div className="grid grid-cols-[1.6fr_0.8fr_1.2fr] gap-1.5">
              <input className={INPUT} value={decision.street} onChange={(e) => onChange({ street: e.target.value })} placeholder={t('customers.street')} />
              <input className={INPUT} value={decision.plz} onChange={(e) => onChange({ plz: e.target.value })} placeholder={t('customers.plz')} />
              <input className={INPUT} value={decision.ort} onChange={(e) => onChange({ ort: e.target.value })} placeholder={t('customers.ort')} />
            </div>
          </div>
        )}
        {decision.action === 'skip' && (
          <span className="text-[12px] text-slate-400">{t('customers.skipHint')}</span>
        )}
      </div>
    </div>
  );
}

export default CustomerStep;
